"use client";

import { useSupabaseClient } from "@/components/SupabaseProvider";
import { SupabaseClient } from "@supabase/supabase-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { Database } from "../types/supabase";

import type { VideoModel } from "@ziggapp/klap-core/video";
import { PLAYGROUND_PROJECT_ID } from "../const/projectConts";
import {
  isContentUpdate,
  isMetadataOnlyRowChange,
  VideoRow,
  VideoSaveConflictError,
} from "../helpers/videoRevision";
import { klap } from "../utils/klap";

/**
 * The row the cached model of each video came from, keyed by id, exactly as
 * PostgREST returned it. A content save is accepted only while the row still
 * carries that `updated_at`; the trigger writes microseconds that
 * `VideoModel.updatedAt` (a JS Date) cannot hold, so the guard keeps the raw
 * string. Seeded by a cold fetch and advanced by this client's own saves —
 * never by a refetch of warm data, because an open editor may still be
 * showing the copy it loaded, and adopting someone else's version as "seen"
 * would let the next keystroke overwrite it.
 */
const lastSeenRows = new Map<string, VideoRow>();

const fetchVideoRow = async (
  client: SupabaseClient<Database>,
  id: string
): Promise<VideoRow | null> => {
  const { data, error } = await client
    .from("videos_v2")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) throw error;

  return data;
};

/**
 * With `expectedUpdatedAt`, PostgREST only touches the row if nobody saved
 * since this client last saw it, and a stale save comes back as zero rows
 * (`null` here). Never chain `.single()` on this update: it turns the
 * zero-row answer into a 406.
 */
const updateVideoRow = async (
  client: SupabaseClient<Database>,
  id: string,
  update: Partial<VideoModel>,
  expectedUpdatedAt?: string
): Promise<VideoRow | null> => {
  let query = client
    .from("videos_v2")
    .update(klap.video.toRowPatch(update))
    .eq("id", id);

  if (expectedUpdatedAt) query = query.eq("updated_at", expectedUpdatedAt);

  const { data, error } = await query.select();

  if (error) throw error;

  return data[0] ?? null;
};

export const useVideo = (id?: string | null) => {
  const queryKey = ["video", id];
  const supabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  const query = useQuery<VideoModel>({
    queryKey: queryKey,
    queryFn: async () => {
      if (!id) throw new Error("id is required");
      const row = await fetchVideoRow(supabaseClient, id);
      if (!row) throw new Error("Video not found");
      // A cold fetch is what the editor will adopt; a refetch of warm data
      // is not (see `lastSeenRows`).
      if (queryClient.getQueryState(queryKey)?.data === undefined) {
        lastSeenRows.set(id, row);
      }
      return klap.video.fromRow(row);
    },
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationKey: queryKey,
    // Saves to one video run in series, so each carries the `updated_at`
    // the previous one returned instead of racing it with a stale token.
    scope: { id: `video-save:${id}` },
    mutationFn: async (update: Partial<VideoModel>) => {
      if (!id) throw new Error("id is required");

      // The Template editor edits one shared, public row that no user
      // owns: everyone may read it, only its author may update it, so a
      // save from anyone else matches zero rows. That is not a conflict,
      // and the row is nobody's truth either — the editor's local copy
      // is — so the playground saves unguarded and a refused write
      // counts as saved.
      const isPlayground = id === PLAYGROUND_PROJECT_ID;

      // Flags (liked, viewed, generated) patch just their columns: they
      // cannot clobber an edit made elsewhere, so they need neither the
      // merge nor the guard, and a list-page like keeps working after the
      // editor moved on.
      if (!isPlayground && !isContentUpdate(update)) {
        const row = await updateVideoRow(supabaseClient, id, update);
        if (!row) throw new VideoSaveConflictError("deleted");
        return klap.video.fromRow(row);
      }

      const currentVideo = queryClient.getQueryData<VideoModel>(queryKey);

      let payload = update;

      if (currentVideo) {
        const mergedVideo = {
          ...currentVideo,
          ...update,
        };

        // Normalize video after merge to keep config in sync with tracks
        payload = klap.video.reconcile(mergedVideo);
      }

      if (isPlayground) {
        const row = await updateVideoRow(supabaseClient, id, payload);
        // The editor only saves once the model is cached, so a refused
        // write hands back the merged model it just tried to persist.
        return row ? klap.video.fromRow(row) : (payload as VideoModel);
      }

      const lastSeen = lastSeenRows.get(id);
      let row = await updateVideoRow(
        supabaseClient,
        id,
        payload,
        lastSeen?.updated_at
      );

      if (!row) {
        // Someone saved since we loaded, or the row is gone. Read it back
        // to tell a real edit from a metadata bump (export counter,
        // schedule config, like/viewed flags) made while the editor was
        // open: those adopt the new revision and retry once.
        const fresh = await fetchVideoRow(supabaseClient, id);
        if (!fresh) throw new VideoSaveConflictError("deleted");
        if (!lastSeen || !isMetadataOnlyRowChange(lastSeen, fresh)) {
          throw new VideoSaveConflictError("changed");
        }
        posthog?.capture("Video Save Revision Refreshed", { video_id: id });
        row = await updateVideoRow(
          supabaseClient,
          id,
          payload,
          fresh.updated_at
        );
        if (!row) throw new VideoSaveConflictError("changed");
      }

      lastSeenRows.set(id, row);

      return klap.video.fromRow(row);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["folder-videos", data.folderId],
        (prev: VideoModel[] | undefined) => {
          if (!prev) return prev;
          return prev.map((v) => (v.id === id ? data : v));
        }
      );
      queryClient.setQueryData(queryKey, (prev: VideoModel | undefined) => {
        if (!prev) return prev;

        return { ...prev, ...data };
      });
    },
    onError: (_error, data) => {
      queryClient.invalidateQueries({ queryKey: queryKey });
      queryClient.invalidateQueries({
        queryKey: ["folder-videos", data.folderId],
      });
    },
  });

  return {
    ...query,
    update: updateMutation,
  };
};
